import { Card, Typography } from '@mui/material';
import { BooleanField, SelectField, TextField, useTranslate } from 'react-admin';

import { GetB2bContractsOneResponse } from '@Common/types/bo/b2b/contracts';
import { GetBoExternalContractsOneResponse } from '@Common/types/bo_external/contracts';

import { contractDaysIncrementStepChoices } from '@sh4res/bo-common/choices/contracts';
import {
  B2bCampaignsProcessingOptions,
  ShowFields,
  ShowSection,
} from '@sh4res/bo-common/components';
import { Datagrid } from '@sh4res/bo-common/components/Datagrid';
import { UTCDateField } from '@sh4res/bo-common/fields';
import { hasAnyUnusedVacationDaysField } from '@sh4res/bo-common/utils/contracts';

type B2bContractAgreementsRecord = GetBoExternalContractsOneResponse | GetB2bContractsOneResponse;

export const B2bContractAgreements = ({
  record,
  showsProcessingOptionsByCashSource,
}: {
  record: B2bContractAgreementsRecord;
  showsProcessingOptionsByCashSource: boolean;
}) => {
  const translate = useTranslate();
  const hasUnusedVacationDays = hasAnyUnusedVacationDaysField(record.unused_vacation_days);
  return (
    <>
      {!showsProcessingOptionsByCashSource && (
        <B2bCampaignsProcessingOptions cashSource="participation" record={record} />
      )}
      {showsProcessingOptionsByCashSource && (
        <B2bCampaignsProcessingOptions
          isSectionTitleLinkedToCashSource={true}
          cashSource="voluntary_deposit"
          record={record}
        />
      )}
      <ShowSection label="resources.bo_common.b2b.contracts.sections.participation">
        {!record.participation?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.participation?.agreement_starting_date && (
          <>
            <ShowFields<B2bContractAgreementsRecord>
              fields={{
                'participation.agreement_starting_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ParticipationStartDateField" />
                ),
                'participation.agreement_end_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ParticipationEndDateField" />
                ),
                'participation.is_agreement_common_for_group': (
                  <BooleanField data-testid="BoExternalB2bContractAgreements_ParticipationIsCommonField" />
                ),
              }}
            />
            <ShowSection
              label="resources.bo_common.b2b.contracts.sections.participation_distribution"
              noCard={true}>
              <Card sx={{ margin: '0px 15px 15px 15px' }}>
                <Datagrid
                  bulkActionButtons={false}
                  sx={{
                    tr: {
                      height: '50px',
                    },
                  }}
                  sort={{ field: 'id', order: 'ASC' }}
                  data={[
                    {
                      id: 1,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.participation.distribution_uniformity_pct',
                      ),
                      pct: `${record.participation.distribution_uniformity_pct ?? '-'} %`,
                    },
                    {
                      id: 2,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.participation.distribution_salary_pct',
                      ),
                      pct: `${record.participation.distribution_salary_pct ?? '-'} %`,
                    },
                    {
                      id: 3,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.participation.distribution_tenure_pct',
                      ),
                      pct: `${record.participation.distribution_tenure_pct ?? '-'} %`,
                    },
                  ]}>
                  <TextField
                    source="rule"
                    label="resources.bo_common.b2b.contracts.distribution_details.rule"
                    data-testid="BoExternalB2bContractAgreements_ParticipationDistributionRuleField"
                  />
                  <TextField
                    source="pct"
                    label="resources.bo_common.b2b.contracts.distribution_details.pct"
                    data-testid="BoExternalB2bContractAgreements_ParticipationDistributionPctField"
                  />
                </Datagrid>
              </Card>
            </ShowSection>
            {showsProcessingOptionsByCashSource && (
              <B2bCampaignsProcessingOptions cashSource="participation" record={record} />
            )}
          </>
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.profit_sharing">
        {!record.profit_sharing?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.profit_sharing?.agreement_starting_date && (
          <>
            <ShowFields<B2bContractAgreementsRecord>
              fields={{
                'profit_sharing.agreement_starting_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ProfitSharingStartDateField" />
                ),
                'profit_sharing.agreement_end_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ProfitSharingEndDateField" />
                ),
                'profit_sharing.is_agreement_common_for_group': (
                  <BooleanField data-testid="BoExternalB2bContractAgreements_ProfitSharingIsCommonField" />
                ),
              }}
            />
            <ShowSection
              label="resources.bo_common.b2b.contracts.sections.profit_sharing_distribution"
              noCard={true}>
              <Card sx={{ margin: '0px 15px 15px 15px' }}>
                <Datagrid
                  bulkActionButtons={false}
                  sx={{
                    tr: {
                      height: '50px',
                    },
                  }}
                  sort={{ field: 'id', order: 'ASC' }}
                  data={[
                    {
                      id: 1,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.profit_sharing.distribution_uniformity_pct',
                      ),
                      pct: `${record.profit_sharing.distribution_uniformity_pct ?? '-'} %`,
                    },
                    {
                      id: 2,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.profit_sharing.distribution_salary_pct',
                      ),
                      pct: `${record.profit_sharing.distribution_salary_pct ?? '-'} %`,
                    },
                    {
                      id: 3,
                      rule: translate(
                        'resources.bo_common.b2b.contracts.fields.profit_sharing.distribution_tenure_pct',
                      ),
                      pct: `${record.profit_sharing.distribution_tenure_pct ?? '-'} %`,
                    },
                  ]}>
                  <TextField
                    source="rule"
                    label="resources.bo_common.b2b.contracts.distribution_details.rule"
                    data-testid="BoExternalB2bContractAgreements_ProfitSharingDistributionRuleField"
                  />
                  <TextField
                    source="pct"
                    label="resources.bo_common.b2b.contracts.distribution_details.pct"
                    data-testid="BoExternalB2bContractAgreements_ProfitSharingDistributionPctField"
                  />
                </Datagrid>
              </Card>
            </ShowSection>
            {showsProcessingOptionsByCashSource && (
              <B2bCampaignsProcessingOptions cashSource="profit_sharing" record={record} />
            )}
          </>
        )}
      </ShowSection>

      <ShowSection label="resources.bo_common.b2b.contracts.sections.value_sharing">
        {!record.value_sharing?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.value_sharing?.agreement_starting_date && (
          <>
            <ShowFields<B2bContractAgreementsRecord>
              fields={{
                'value_sharing.agreement_starting_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ValueSharingStartDateField" />
                ),
                'value_sharing.agreement_end_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_ValueSharingEndDateField" />
                ),
                'value_sharing.is_agreement_common_for_group': (
                  <BooleanField data-testid="BoExternalB2bContractAgreements_ValueSharingIsCommonField" />
                ),
              }}
            />
            {showsProcessingOptionsByCashSource && (
              <B2bCampaignsProcessingOptions cashSource="value_sharing_bonus" record={record} />
            )}
          </>
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.unused_vacation_days">
        {!hasUnusedVacationDays && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {hasUnusedVacationDays && (
          <>
            <ShowFields<B2bContractAgreementsRecord>
              fields={{
                'unused_vacation_days.agreement_starting_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_UnusedVacationDaysStartDateField" />
                ),
                'unused_vacation_days.agreement_end_date': (
                  <UTCDateField data-testid="BoExternalB2bContractAgreements_UnusedVacationDaysEndDateField" />
                ),
                'unused_vacation_days.is_agreement_common_for_group': (
                  <BooleanField data-testid="BoExternalB2bContractAgreements_UnusedVacationDaysIsCommonField" />
                ),
                'unused_vacation_days.increment_step': (
                  <SelectField
                    data-testid="BoExternalB2bContractAgreements_UnusedVacationDaysIncrementStepField"
                    choices={contractDaysIncrementStepChoices}
                  />
                ),
              }}
            />
            {showsProcessingOptionsByCashSource && (
              <B2bCampaignsProcessingOptions cashSource="unused_vacation_days" record={record} />
            )}
          </>
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.monetized_days_off">
        {!record.monetized_days_off && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.monetized_days_off && (
          <ShowFields<B2bContractAgreementsRecord>
            fields={{
              'monetized_days_off.increment_step': (
                <SelectField
                  data-testid="BoExternalB2bContractAgreements_MonetizedDaysOffIncrementStepField"
                  choices={contractDaysIncrementStepChoices}
                />
              ),
            }}
          />
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.pee_agreement">
        {!record.pee_agreement?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.pee_agreement?.agreement_starting_date && (
          <ShowFields<B2bContractAgreementsRecord>
            fields={{
              'pee_agreement.agreement_starting_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PeeAgreementStartDateField" />
              ),
              'pee_agreement.agreement_end_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PeeAgreementEndDateField" />
              ),
              'pee_agreement.is_agreement_common_for_group': (
                <BooleanField data-testid="BoExternalB2bContractAgreements_PeeAgreementIsCommonField" />
              ),
            }}
          />
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.pereco_agreement">
        {!record.pereco_agreement?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.pereco_agreement?.agreement_starting_date && (
          <ShowFields<B2bContractAgreementsRecord>
            fields={{
              'pereco_agreement.agreement_starting_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PerecoAgreementStartDateField" />
              ),
              'pereco_agreement.agreement_end_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PerecoAgreementEndDateField" />
              ),
              'pereco_agreement.is_agreement_common_for_group': (
                <BooleanField data-testid="BoExternalB2bContractAgreements_PerecoAgreementIsCommonField" />
              ),
            }}
          />
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.pero_agreement">
        {!record.pero_agreement?.agreement_starting_date && (
          <Typography variant="body1" sx={{ padding: '13px 30px 13px 15px' }}>
            {translate('resources.bo_common.b2b.contracts.sections.no_agreement')}
          </Typography>
        )}
        {record.pero_agreement?.agreement_starting_date && (
          <ShowFields<B2bContractAgreementsRecord>
            fields={{
              'pero_agreement.agreement_starting_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PeroAgreementStartDateField" />
              ),
              'pero_agreement.agreement_end_date': (
                <UTCDateField data-testid="BoExternalB2bContractAgreements_PeroAgreementEndDateField" />
              ),
              'pero_agreement.is_agreement_common_for_group': (
                <BooleanField data-testid="BoExternalB2bContractAgreements_PeroAgreementIsCommonField" />
              ),
            }}
          />
        )}
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.contracts.sections.transfer">
        <ShowFields<B2bContractAgreementsRecord>
          fields={{
            'transfer.is_competition_transfer': (
              <BooleanField data-testid="BoExternalB2bContractAgreements_TransferIsCompetitionField" />
            ),
            'transfer.is_soles_transfer': (
              <BooleanField data-testid="BoExternalB2bContractAgreements_TransferIsSolesField" />
            ),
          }}
        />
      </ShowSection>
    </>
  );
};
